import { memo, useEffect, useRef, useState } from "react";
import type { MediaKind } from "@wedding/shared";
import { cn } from "../../lib/utils";
import { cdnUrl } from "../../lib/cdn";

export interface StageMedia {
  id: string;
  url: string;
  kind: MediaKind;
}

const FADE_MS = 1000;
// Below this the seek isn't worth the decode hiccup — just play from the top.
const MIN_SEEK_MS = 500;

interface CinematicStageProps {
  current: StageMedia | null;
  /** Slide duration in ms — drives the Ken Burns timing. */
  slideMs: number;
  reduceMotion: boolean;
  /** Server-synchronized pause — freezes video playback + Ken Burns. */
  paused: boolean;
  /** Elapsed ms into the current slide (late join / reconnect) — videos seek here. */
  startOffsetMs: number;
  /** Probed video duration, forwarded to the sync server as a duration_report. */
  onVideoMeta?: (mediaId: string, durationMs: number) => void;
  /** Local per-screen mute — only the active (phase "in") layer can sound. */
  muted: boolean;
}

/**
 * Full-bleed cinema surface — NO frame, NO card, NO rounded corners:
 *  - landscape photos & videos fill the entire screen (object-cover),
 *  - portraits are protected (object-contain) with the colour-bleed backdrop
 *    filling the sides,
 *  - pronounced Ken Burns push, true cross-dissolve, cinematic vignette + top/
 *    bottom scrims laid OVER the media, and an animated film-grain overlay.
 *
 * Always a dark theatre surface regardless of app theme (projection-first).
 *
 * Advancement is server-driven (SlideshowRoom DO): a finished video holds its
 * last frame until the next slide_state arrives — do NOT advance locally.
 */
function MediaLayer({
  media,
  phase,
  reduceMotion,
  slideMs,
  paused,
  startOffsetMs,
  onVideoMeta,
  muted,
}: {
  media: StageMedia;
  phase: "in" | "out";
  reduceMotion: boolean;
  slideMs: number;
  paused: boolean;
  startOffsetMs: number;
  onVideoMeta?: (mediaId: string, durationMs: number) => void;
  muted: boolean;
}) {
  // Default to contain (no crop flash); switch to cover once we know it's landscape.
  const [fit, setFit] = useState<"cover" | "contain">("contain");
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Server-synchronized pause/resume for the active video.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || phase !== "in") return;
    if (paused) v.pause();
    else v.play().catch(() => { /* autoplay policies — muted, should not happen */ });
  }, [paused, phase]);

  // Local per-screen mute. The outgoing (cross-dissolve) layer stays muted so
  // two videos never overlap in sound. React doesn't reliably set `muted` as a
  // property post-mount → set it imperatively.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = phase === "in" ? muted : true;
  }, [muted, phase]);

  return (
    <div className={cn("absolute inset-0", phase === "in" ? "ss-fade-in" : "ss-fade-out")}>
      {media.kind === "image" ? (
        <img
          src={cdnUrl(media.url, { w: 2048 })}
          alt=""
          onLoad={(e) => {
            const img = e.currentTarget;
            setFit(img.naturalHeight > img.naturalWidth * 1.05 ? "contain" : "cover");
          }}
          className={cn(
            "w-full h-full object-center",
            fit === "cover" ? "object-cover" : "object-contain",
            !reduceMotion && "ss-kenburns-css",
          )}
          style={{
            ["--dur" as string]: `${slideMs + 1500}ms`,
            animationPlayState: paused ? "paused" : undefined,
          } as React.CSSProperties}
        />
      ) : (
        <video
          ref={videoRef}
          src={media.url}
          className="w-full h-full object-cover object-center"
          autoPlay={!paused}
          muted
          playsInline
          preload="auto"
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            if (Number.isFinite(v.duration) && v.duration > 0) {
              onVideoMeta?.(media.id, Math.round(v.duration * 1000));
            }
            if (phase === "in" && Number.isFinite(v.duration)) {
              if (startOffsetMs > MIN_SEEK_MS) {
                // Late join / reconnect: jump to the synchronized position.
                v.currentTime = Math.min(startOffsetMs / 1000, Math.max(0, v.duration - 0.1));
              } else if (paused) {
                // Paused at slide start (autoPlay off): a tiny seek forces a
                // frame decode + paint so the video isn't a black rectangle.
                v.currentTime = 0.04;
              }
            }
          }}
        />
      )}
    </div>
  );
}

function CinematicStageImpl({
  current,
  slideMs,
  reduceMotion,
  paused,
  startOffsetMs,
  onVideoMeta,
  muted,
}: CinematicStageProps) {
  // Double-buffer for the cross-dissolve; retire the outgoing slide afterwards.
  const [pair, setPair] = useState<{ prev: StageMedia | null; cur: StageMedia | null }>({
    prev: null,
    cur: null,
  });

  useEffect(() => {
    setPair((p) => (p.cur?.id === current?.id ? p : { prev: p.cur, cur: current ?? null }));
  }, [current]);

  useEffect(() => {
    if (!pair.prev) return;
    const id = setTimeout(() => setPair((p) => ({ prev: null, cur: p.cur })), FADE_MS);
    return () => clearTimeout(id);
  }, [pair.prev]);

  return (
    <div className="absolute inset-0 overflow-hidden bg-[#0b0a09]">
      {/* Colour-bleed backdrop (visible behind portraits / cover edges) */}
      {pair.cur?.kind === "image" && (
        <div
          key={`bg-${pair.cur.id}`}
          className={cn(
            "absolute inset-0 bg-cover bg-center blur-3xl scale-[1.2] opacity-50 transition-opacity duration-1000",
            !reduceMotion && "ss-ambient",
          )}
          style={{
            backgroundImage: `url(${cdnUrl(pair.cur.url, { w: 64 })})`,
            animationPlayState: paused ? "paused" : undefined,
          }}
          aria-hidden
        />
      )}
      <div className="absolute inset-0 bg-[#0b0a09]/35" aria-hidden />

      {/* Full-bleed media with cross-dissolve */}
      <div className="absolute inset-0">
        {pair.prev && (
          <MediaLayer
            key={pair.prev.id}
            media={pair.prev}
            phase="out"
            reduceMotion={reduceMotion}
            slideMs={slideMs}
            paused={paused}
            startOffsetMs={0}
            onVideoMeta={onVideoMeta}
            muted={muted}
          />
        )}
        {pair.cur && (
          <MediaLayer
            key={pair.cur.id}
            media={pair.cur}
            phase="in"
            reduceMotion={reduceMotion}
            slideMs={slideMs}
            paused={paused}
            startOffsetMs={startOffsetMs}
            onVideoMeta={onVideoMeta}
            muted={muted}
          />
        )}
      </div>

      {/* Cinematic vignette + scrims, laid OVER the photo */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, transparent 48%, rgba(0,0,0,0.5) 100%)" }}
        aria-hidden
      />
      <div className="absolute inset-x-0 top-0 h-[22%] bg-gradient-to-b from-black/55 to-transparent pointer-events-none" aria-hidden />
      <div className="absolute inset-x-0 bottom-0 h-[45%] bg-gradient-to-t from-black/85 via-black/25 to-transparent pointer-events-none" aria-hidden />

      {/* Film grain */}
      <div className="ss-grain absolute inset-0 pointer-events-none" aria-hidden />
    </div>
  );
}

export const CinematicStage = memo(CinematicStageImpl);
